/**
 * Data-export screen — Owner-only (Requirement 25.1–25.4, 25.8).
 *
 * Lets an Owner request a full tenant data export, then tracks and surfaces the
 * status of that request. Data export is a *backend* concept (a machine-readable
 * export scoped strictly to the requesting `tenant_id`, built asynchronously and
 * announced when ready — Req 17). Per the module boundary, `web` imports no
 * backend runtime: the actual request call and any retrieve action are INJECTED
 * as props, and this screen only renders controls and presents the Platform's
 * responses through the accessible {@link UserMessage}.
 *
 * Requirement mapping:
 *   - 25.1 Owner sees a control to request a full tenant data export.
 *   - 25.2 The screen presents the status of the request; when the Platform
 *          reports the export is available it shows an "available" User_Message
 *          together with a retrieve control. (Also Req 17.4 — notify when ready.)
 *   - 25.3 If the Platform reports the export could not be completed, an error
 *          User_Message states what/why/next. (Req 17.5 — EXPORT_FAILED.)
 *   - 25.4 If the request is denied/failed at submission, an error User_Message
 *          states what/why/next.
 *   - 25.8 A non-Owner sees only a restricted User_Message and no controls;
 *          authorization is enforced by the Platform regardless — the hidden
 *          controls are never the sole gate.
 *   - 22.11 Every user-facing string resolves through the i18n catalog (`useT`).
 *   - 26.x  All feedback is rendered through {@link UserMessage}.
 *
 * SSR note: the Owner request control is in the initial render output before any
 * interaction, so the server-rendered HTML contains real content (Req 21.1).
 * Async results update local state on the client after hydration.
 */
import { useCallback, useState, type ReactElement } from "react";
// Type-only import from the shared foundation contract (web imports types only).
import type { ErrorEnvelope } from "@avos/foundation";
import { useT } from "../i18n/index.js";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../components/UserMessage.js";

/** The four tenant RBAC roles (Req 12.1). Only `Owner` may use this screen. */
export type TenantRole = "Owner" | "Manager" | "Staff" | "Accountant";

/**
 * The result of the injected export-request call.
 *
 * On success the backend has accepted the request; the export is built
 * asynchronously and announced when ready (Req 17.4 / 25.2). On failure it
 * carries the stable {@link ErrorEnvelope} (e.g. `EXPORT_FAILED`) the screen
 * maps to a User_Message via {@link toUserMessage} (Req 25.3, 25.4).
 */
export type RequestExportResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: ErrorEnvelope };

/**
 * The injected current status of the most recent export, surfaced by the host
 * (Req 25.2). `available` means the Platform reported the export is ready; when
 * available, `retrievalUrl` is an optional human/host-supplied handle the
 * retrieve control uses.
 */
export interface DataExportStatus {
  /** Whether the Platform has reported the export is available to retrieve. */
  readonly available: boolean;
  /** Optional host-supplied retrieval handle shown/used by the retrieve control. */
  readonly retrievalUrl?: string;
}

/** Props for {@link DataExportScreen}. */
export interface DataExportScreenProps {
  /**
   * The signed-in user's tenant role. Only `Owner` sees the controls (Req 25.8).
   * Optional and fail-closed: when the host has not injected a role (e.g. the
   * bare screen-registry render used by the SSR shell), it defaults to a
   * NON-Owner view so no controls leak. The host (and tests) inject the real role.
   */
  readonly role?: TenantRole;
  /**
   * The injected current export status (Req 25.2). When `available`, the screen
   * shows the "available" User_Message and the retrieve control.
   */
  readonly status?: DataExportStatus;
  /**
   * Injected export-request call. Web imports no backend runtime, so the actual
   * request is provided by the host. Optional so the screen can render its
   * control in SSR/isolation without a wired backend; the request action is
   * only enabled once a handler is present.
   */
  readonly onRequestExport?: () => Promise<RequestExportResult>;
  /**
   * Injected retrieve action, invoked when the Owner retrieves an available
   * export (Req 25.2). Optional.
   */
  readonly onRetrieveExport?: () => void;
}

/** What kind of feedback the screen is currently showing. */
type Feedback =
  | { readonly kind: "none" }
  | { readonly kind: "error"; readonly model: UserMessageModel }
  | { readonly kind: "status"; readonly model: UserMessageModel };

export default function DataExportScreen({
  role = "Staff",
  status,
  onRequestExport,
  onRetrieveExport,
}: DataExportScreenProps = {}): ReactElement {
  const t = useT();

  const [feedback, setFeedback] = useState<Feedback>({ kind: "none" });
  // Whether the current session has submitted a request (drives the "requested"
  // status line). Never true during SSR.
  const [requested, setRequested] = useState<boolean>(false);
  // Whether an injected call is in flight (disables controls, prevents double
  // submit). Never true during SSR.
  const [busy, setBusy] = useState<boolean>(false);

  const handleRequest = useCallback(async () => {
    if (onRequestExport === undefined) {
      return;
    }
    setBusy(true);
    try {
      const result = await onRequestExport();
      if (result.ok) {
        // Req 25.2 / 17.4: request accepted; the export will be built and
        // announced when ready. Confirm the request was received.
        setRequested(true);
        setFeedback({
          kind: "status",
          model: {
            what: t("admin.export.requested.what"),
            why: t("admin.export.requested.why"),
            next: t("admin.export.requested.next"),
          },
        });
      } else {
        // Req 25.3 / 25.4 / 17.5: request denied/failed; present the Platform's
        // reason (EXPORT_FAILED and friends map through toUserMessage).
        setFeedback({ kind: "error", model: toUserMessage(result.error) });
      }
    } finally {
      setBusy(false);
    }
  }, [onRequestExport, t]);

  // Non-Owner: restricted message only, no controls (Req 25.8). The Platform
  // still enforces authorization; the hidden controls are never the sole gate.
  if (role !== "Owner") {
    return (
      <section
        aria-labelledby="screen-heading"
        data-screen="dataExport"
        data-restricted="true"
      >
        <h1 id="screen-heading">{t("admin.export.title")}</h1>
        <UserMessage
          severity="status"
          message={{
            what: t("admin.export.restricted.what"),
            why: t("admin.export.restricted.why"),
            next: t("admin.export.restricted.next"),
          }}
        />
      </section>
    );
  }

  const available = status?.available ?? false;

  return (
    <section aria-labelledby="screen-heading" data-screen="dataExport">
      <h1 id="screen-heading">{t("admin.export.title")}</h1>
      <p>{t("admin.export.prompt")}</p>

      {/* Per-request status line (Req 25.2). */}
      <p
        data-testid="export-status"
        data-requested={String(requested)}
        data-available={String(available)}
      >
        {available
          ? t("admin.export.statusAvailable")
          : requested
            ? t("admin.export.statusRequested")
            : t("admin.export.statusNone")}
      </p>

      {/* Request control (Req 25.1). */}
      <button
        type="button"
        data-testid="request-export"
        disabled={busy}
        onClick={() => void handleRequest()}
      >
        {t("admin.export.request")}
      </button>

      {/* When the Platform reports an export is available, show an "available"
          User_Message together with a retrieve control (Req 25.2). */}
      {available ? (
        <div className="data-export__available">
          <UserMessage
            severity="status"
            message={{
              what: t("admin.export.available.what"),
              why: t("admin.export.available.why"),
              next: t("admin.export.available.next"),
            }}
          />
          <button
            type="button"
            data-testid="retrieve-export"
            onClick={() => onRetrieveExport?.()}
          >
            {t("admin.export.retrieve")}
          </button>
        </div>
      ) : null}

      {feedback.kind !== "none" ? (
        <UserMessage
          severity={feedback.kind === "error" ? "error" : "status"}
          message={feedback.model}
        />
      ) : null}
    </section>
  );
}
